import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  loadCandidateConversation,
  loadOlderCandidateMessages,
  sendCandidateMessage,
  markCandidateRead,
  refreshCandidateConversation,
  upsertChatMessage,
  setChatPresence,
  applyReadReceipt,
  resetChat,
} from '../../Redux/ActionCreators/chatAction';
import { fetchTenantBranding } from '../../Redux/ActionCreators/qualifyQuestionsAction';
import { handleRichEditorPaste } from './pasteSanitizer';
import './CandidateChat.scss';

const SendIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
  </svg>
);

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
  </svg>
);

const ResendIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
    <polyline points="1 4 1 10 7 10" />
    <path d="M3.51 15a9 9 0 1 0 .49-3.56" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const resolveFileType = (name = '', mimeType = '') => {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (ext === 'pdf' || mimeType.includes('pdf'))
    return { color: '#991b1b', bg: '#fee2e2', label: 'PDF' };
  if (['xlsx', 'xls', 'csv'].includes(ext) || mimeType.includes('sheet') || mimeType.includes('excel') || mimeType.includes('csv'))
    return { color: '#15803d', bg: '#dcfce7', label: 'XLS' };
  if (['doc', 'docx'].includes(ext) || mimeType.includes('word') || mimeType.includes('doc'))
    return { color: '#1e40af', bg: '#dbeafe', label: 'DOC' };
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext) || mimeType.includes('image'))
    return { color: '#92400e', bg: '#fef3c7', label: 'IMG' };
  if (['txt', 'md', 'log'].includes(ext) || mimeType.includes('text') || mimeType.includes('plain'))
    return { color: '#374151', bg: '#f3f4f6', label: 'TXT' };
  if (['ppt', 'pptx'].includes(ext))
    return { color: '#c2410c', bg: '#ffedd5', label: 'PPT' };
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext))
    return { color: '#6d28d9', bg: '#ede9fe', label: 'ZIP' };
  return { color: '#6b7280', bg: '#f3f4f6', label: ext.toUpperCase().slice(0, 4) || 'FILE' };
};

const FileTypeIcon = ({ name, mimeType }) => {
  const { color, bg, label } = resolveFileType(name, mimeType);
  return (
    <svg width="36" height="42" viewBox="0 0 36 42" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 0h20l12 12v26a4 4 0 0 1-4 4H4a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z" fill={bg} />
      <path d="M24 0l12 12H28a4 4 0 0 1-4-4V0z" fill={color} fillOpacity="0.35" />
      <text x="18" y="30" textAnchor="middle" fill={color} fontSize="8.5" fontWeight="800" fontFamily="Arial, sans-serif" letterSpacing="0.5">{label}</text>
    </svg>
  );
};

const handleAttachmentDownload = async (url, fileName) => {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
  } catch {
    window.open(url, '_blank');
  }
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const getDateLabel = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
};

const isHtmlEmpty = (html) => {
  if (!html) return true;
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, '').trim() === '';
};

const formatChannelLabel = (ch) => {
  if (ch === 'messenger') return 'Messenger';
  if (ch === 'in_app') return 'In-app';
  return ch || 'In-app';
};

const CandidateChat = ({ jobCandidateId: jobCandidateIdProp }) => {
  const { jobCandidateId: jobCandidateIdParam } = useParams();
  const jobCandidateId = jobCandidateIdProp || jobCandidateIdParam;
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const conversation = useSelector((state) => state.chat.conversation);
  const messages = useSelector((state) => state.chat.messages);
  const tenantBranding = useSelector((state) => state.qualifyQuestions?.tenantBranding);
  const isWhiteLabel = Boolean(tenantBranding?.is_white_label);
  const accentColor = isWhiteLabel && tenantBranding?.colour ? tenantBranding.colour : '#F75891';

  useEffect(() => {
    const token = searchParams.get('t');
    if (!token) return;
    dispatch(fetchTenantBranding(token));
  }, [searchParams, dispatch]);

  useEffect(() => {
    if (isWhiteLabel && tenantBranding?.colour) {
      document.documentElement.style.setProperty('--qualify-accent-color', tenantBranding.colour);
    }
  }, [isWhiteLabel, tenantBranding?.colour]);

  const [messageBody, setMessageBody] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [wsBadge, setWsBadge] = useState('off');
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [failedMessage, setFailedMessage] = useState(null);

  const wsRef = useRef(null);
  const wsReconnectTimer = useRef(null);
  const wsIntentionalClose = useRef(false);
  const wsReconnectSync = useRef(false);
  const lastMarkedReadId = useRef(null);
  const messagesContainerRef = useRef(null);
  const isFetchingOlderRef = useRef(false);
  const chatEditorRef = useRef(null);
  const messageBodyRef = useRef('');

  const conversationId = conversation?.conversation_id;

  useEffect(() => {
    const handler = () => {
      clearTimeout(wsReconnectTimer.current);
      wsIntentionalClose.current = true;
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    };
    window.addEventListener('APP_LOGOUT', handler);
    return () => window.removeEventListener('APP_LOGOUT', handler);
  }, []);

  // Reset per-chat state when switching conversations
  useEffect(() => {
    setMessageBody('');
    messageBodyRef.current = '';
    if (chatEditorRef.current) chatEditorRef.current.innerHTML = '';
    setHasMoreMessages(true);
    setLoadingOlder(false);
    isFetchingOlderRef.current = false;
    lastMarkedReadId.current = null;
    try {
      const stored = localStorage.getItem(`gappeo_failed_cc_${jobCandidateId}`);
      setFailedMessage(stored ? JSON.parse(stored) : null);
    } catch {
      setFailedMessage(null);
    }
  }, [jobCandidateId]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await dispatch(loadCandidateConversation(jobCandidateId));
      setLoading(false);
    };
    init();
    return () => {
      disconnectWs();
      dispatch(resetChat());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobCandidateId]);

  useEffect(() => {
    if (!conversationId) return;
    connectWs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // column-reverse: browser anchors to visual bottom on mount; only nudge if user is near bottom
  useEffect(() => {
    if (!messages.length) return;
    const container = messagesContainerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const isNearBottom = scrollTop >= scrollHeight - clientHeight - 150;
    if (isNearBottom) container.scrollTo({ top: scrollHeight - clientHeight, behavior: 'smooth' });
  }, [messages]);

  // Auto-mark recruiter messages as read (messages[0] = newest in desc order)
  useEffect(() => {
    if (!messages.length || !conversationId) return;
    const newest = messages[0];
    if (newest?.sender_type === 'recruiter' && newest.id !== lastMarkedReadId.current) {
      lastMarkedReadId.current = newest.id;
      dispatch(markCandidateRead(jobCandidateId, newest.id)).then(() => {
        dispatch(refreshCandidateConversation(jobCandidateId));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const buildWsUrl = () =>
    `${process.env.REACT_APP_WS_URL}?job_candidate_id=${encodeURIComponent(jobCandidateId)}`;

  const joinGroup = (ws, convId) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ action: 'join', group: `candidate-conversation-${convId}` }));
  };

  const syncFromServer = async () => {
    await dispatch(loadCandidateConversation(jobCandidateId));
  };

  const connectWs = () => {
    if (!conversationId) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      joinGroup(wsRef.current, conversationId);
      return;
    }
    disconnectWs();

    const ws = new WebSocket(buildWsUrl());
    wsRef.current = ws;

    ws.onopen = async () => {
      setWsBadge('on');
      joinGroup(ws, conversationId);
      if (wsReconnectSync.current) {
        wsReconnectSync.current = false;
        await syncFromServer();
      }
    };

    ws.onclose = () => {
      wsRef.current = null;
      setWsBadge('off');
      const intentional = wsIntentionalClose.current;
      wsIntentionalClose.current = false;
      if (!intentional && conversationId) {
        wsReconnectSync.current = true;
        wsReconnectTimer.current = setTimeout(connectWs, 3000);
      }
    };

    ws.onerror = () => {};

    ws.onmessage = (ev) => {
      try {
        handleWsEvent(JSON.parse(ev.data));
      } catch (_) {}
    };
  };

  const disconnectWs = () => {
    clearTimeout(wsReconnectTimer.current);
    wsIntentionalClose.current = true;
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  };

  const handleWsEvent = (data) => {
    if (data.type === 'your_id') return;

    if (data.type === 'group_members') {
      if (data.group === `candidate-conversation-${conversationId}`) {
        dispatch(setChatPresence(!!data.recruiter_online, !!data.candidate_online));
        setWsBadge('live');
      }
      return;
    }
    if (data.type === 'conversation_message' && data.message) {
      dispatch(upsertChatMessage(data.message));
      if (data.message.sender_type === 'recruiter') {
        dispatch(markCandidateRead(jobCandidateId, data.message.id));
        dispatch(refreshCandidateConversation(jobCandidateId));
      }
      return;
    }
    if (data.type === 'message_read' && data.participant_type === 'recruiter') {
      dispatch(applyReadReceipt('candidate', data.last_read_message_id));
      dispatch(refreshCandidateConversation(jobCandidateId));
    }
  };

  // Oldest message is last in desc array; use its id as cursor
  const fetchOlderMessages = async () => {
    if (!hasMoreMessages || isFetchingOlderRef.current || !messages.length) return;
    const oldest = messages[messages.length - 1];
    if (!oldest) return;
    isFetchingOlderRef.current = true;
    setLoadingOlder(true);
    const result = await dispatch(loadOlderCandidateMessages(jobCandidateId, oldest.id));
    setLoadingOlder(false);
    isFetchingOlderRef.current = false;
    if (result?.success && !result.hasMore) setHasMoreMessages(false);
  };

  // column-reverse: scrollTop=0 means visual top (oldest messages)
  const handleMessagesScroll = (e) => {
    const container = e.currentTarget;
    if (container.scrollTop <= 50) fetchOlderMessages();
  };

  const handleSend = async () => {
    const htmlBody = messageBodyRef.current;
    if (isHtmlEmpty(htmlBody) || sending || failedMessage) return;
    setSending(true);
    messageBodyRef.current = '';
    setMessageBody('');
    if (chatEditorRef.current) chatEditorRef.current.innerHTML = '';
    const res = await dispatch(sendCandidateMessage(jobCandidateId, htmlBody, 'in_app'));
    setSending(false);
    if (res?.success) {
      dispatch(refreshCandidateConversation(jobCandidateId));
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        await syncFromServer();
      }
    } else {
      const failed = { text: htmlBody, timestamp: new Date().toISOString() };
      setFailedMessage(failed);
      localStorage.setItem(`gappeo_failed_cc_${jobCandidateId}`, JSON.stringify(failed));
    }
  };

  const handleResend = async () => {
    if (!failedMessage || sending) return;
    const { text } = failedMessage;
    setFailedMessage(null);
    localStorage.removeItem(`gappeo_failed_cc_${jobCandidateId}`);
    setSending(true);
    const res = await dispatch(sendCandidateMessage(jobCandidateId, text, 'in_app'));
    setSending(false);
    if (res?.success) {
      dispatch(refreshCandidateConversation(jobCandidateId));
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        await syncFromServer();
      }
    } else {
      const failed = { text, timestamp: new Date().toISOString() };
      setFailedMessage(failed);
      localStorage.setItem(`gappeo_failed_cc_${jobCandidateId}`, JSON.stringify(failed));
    }
  };

  const handleDeleteFailed = () => {
    setFailedMessage(null);
    localStorage.removeItem(`gappeo_failed_cc_${jobCandidateId}`);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Build grouped items with date separators (desc order + column-reverse algorithm)
  // Separators are placed AFTER the last message of each day in DOM order
  // so column-reverse renders them ABOVE each day's group visually
  const groupedItems = [];
  let currentDayStr = null;
  let currentDayCreatedAt = null;
  messages.forEach((msg) => {
    const msgDayStr = msg.created_at ? new Date(msg.created_at).toDateString() : '';
    if (msgDayStr !== currentDayStr) {
      if (currentDayStr !== null)
        groupedItems.push({ type: 'date', label: getDateLabel(currentDayCreatedAt) });
      currentDayStr = msgDayStr;
      currentDayCreatedAt = msg.created_at;
    }
    groupedItems.push({ type: 'msg', msg });
  });
  if (currentDayStr !== null)
    groupedItems.push({ type: 'date', label: getDateLabel(currentDayCreatedAt) });

  const recruiterName = conversation?.recruiter_display_name || 'Recruiter';
  const recruiterInitials = recruiterName.charAt(0).toUpperCase();
  const recruiterOnline = conversation?.recruiter_online;
  const unreadCount = conversation?.unread_count || 0;

  return (
    <div className="cc-shell">
      <div className="cc-card">
        <div className="cc-header">
          <div className="cc-header-brand">
            <div className="cc-avatar">{recruiterInitials}</div>
            <div className="cc-header-info">
              <div className="cc-recruiter-name">{recruiterName}</div>
              <div className="cc-status-row">
                <span className="cc-team"><img src='/images/bag-icon-black.svg' alt='' width={12} height={12} />&nbsp;{conversation?.job_title}&nbsp;/&nbsp;<img src='/images/company-icon-black.svg' alt='' width={12} height={12} />&nbsp;{conversation?.job_company_name}</span>
              </div>
            </div>
          </div>
        </div>

        {unreadCount > 0 && (
          <div className="cc-unread-banner">
            {unreadCount} new message{unreadCount > 1 ? 's' : ''} from recruiter
          </div>
        )}

        {loading ? (
          <div className="cc-loading">
            <div className="spinner-border" role="status" style={{ width: '1.5rem', height: '1.5rem', color: accentColor }}>
              <span className="visually-hidden">Loading…</span>
            </div>
          </div>
        ) : (
          <div className="cc-messages" ref={messagesContainerRef} onScroll={handleMessagesScroll}>
            {failedMessage && (
              <div className="cc-message out cc-message--failed">
                <div>
                  <div className="cc-bubble cc-bubble--failed">{failedMessage.text}</div>
                  <div className="cc-msg-meta cc-failed-meta">
                    <span className="cc-failed-label">Failed to send</span>
                    <button className="cc-failed-btn cc-failed-btn--resend" onClick={handleResend} disabled={sending} title="Resend message">
                      <ResendIcon /> Resend
                    </button>
                    <button className="cc-failed-btn cc-failed-btn--delete" onClick={handleDeleteFailed} title="Delete message">
                      <TrashIcon />
                    </button>
                  </div>
                </div>
              </div>
            )}
            {messages.length === 0 && !failedMessage && (
              <p className="cc-empty">No messages yet. Say hello!</p>
            )}
            {groupedItems.map((item, idx) => {
              if (item.type === 'date') {
                return (
                  <div key={`date-${idx}`} className="cc-date-separator">
                    <span>{item.label}</span>
                  </div>
                );
              }
              const msg = item.msg;
              const isOut = msg.sender_type === 'candidate';
              const ticks = isOut ? (msg.is_read_by_other ? ' ✓✓' : ' ✓') : '';
              const hasAttachments = msg.attachments && msg.attachments.length > 0;
              return (
                <div key={msg.id} className={`cc-message ${isOut ? 'out' : 'in'}`}>
                  {!isOut && (
                    <div className="cc-msg-avatar">{recruiterInitials}</div>
                  )}
                  <div>
                    {hasAttachments ? (
                      <div className="cc-bubble cc-bubble--attachment">
                        {msg.attachments.map((att) => (
                          <div key={att.id} className="cc-attachment">
                            <div className="cc-att-icon">
                              <FileTypeIcon name={att.file_name} mimeType={att.content_type} />
                            </div>
                            <a
                              className="cc-att-name"
                              href={att.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {att.file_name}
                            </a>
                            <button
                              className="cc-att-download"
                              title="Download"
                              onClick={() => handleAttachmentDownload(att.file_url, att.file_name)}
                            >
                              <DownloadIcon />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="cc-bubble">
                        <span dangerouslySetInnerHTML={{ __html: msg.body_text || '' }} />
                      </div>
                    )}
                    <div className="cc-msg-meta">
                      {!isOut && <span>Recruiter</span>}
                      <span> · {formatTime(msg.created_at)}</span>
                      {ticks && <span className={`cc-ticks${msg.is_read_by_other ? ' read' : ''}`}>{ticks}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
            {/* These sit at DOM end = visual top with column-reverse */}
            {loadingOlder && (
              <div className="cc-older-loading">
                <div className="spinner-border" role="status" style={{ width: '1rem', height: '1rem', color: accentColor }}>
                  <span className="visually-hidden">Loading…</span>
                </div>
              </div>
            )}
            {!hasMoreMessages && !loadingOlder && (
              <div className="cc-conversation-start">Beginning of conversation</div>
            )}
          </div>
        )}

        <div className="cc-composer">
          <div className="cc-composer-row">
            <div className="cc-rich-input-wrap">
              <div
                ref={chatEditorRef}
                contentEditable={!failedMessage && !sending}
                suppressContentEditableWarning
                className="cc-rich-input"
                data-placeholder={failedMessage ? 'Resolve the failed message above first…' : 'Message recruiter…'}
                onInput={(e) => {
                  const html = e.currentTarget.innerHTML;
                  messageBodyRef.current = html;
                  setMessageBody(html);
                }}
                onPaste={(e) => handleRichEditorPaste(e, chatEditorRef.current, (html) => {
                  messageBodyRef.current = html;
                  setMessageBody(html);
                })}
                onKeyDown={handleKeyDown}
              />
            </div>
            <button
              className="cc-send-btn"
              onClick={handleSend}
              disabled={isHtmlEmpty(messageBody) || sending || !!failedMessage}
              aria-label="Send"
            >
              <SendIcon />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateChat;
